import React from 'react';
import { InputBaseComponentProps, TextField as MUITextField } from '@mui/material';
import identity from 'lodash/identity';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { NumericFormat } from "react-number-format";

import { Label } from '@src/components/Typography';
import { VStack } from '@src/components/VStack';
import { palette } from '@src/theme';
import { getTestId } from '@src/utils/testIds';
import { BASE_TEXT_FIELD_HEIGHT } from '../TextField/util';
import { CurrencyFieldControllerProps, CurrencyFieldProps } from './interface';

const CurrencyFieldInput = (props: CurrencyFieldProps & CurrencyFieldControllerProps) => {
  const {
    disabled,
    error,
    formatOnChange = identity,
    helperText,
    label,
    name,
    onChange,
    parseValue = identity,
    parseError = parseValue,
    placeholder,
    rows,
    value: fieldValue,
  } = props;

  const parsedValue = fieldValue || fieldValue === 0 ? parseValue(fieldValue) / 100 : undefined;

  const getHelperText = (error: FieldError | undefined) => {
    const parsedError = error ? parseError(error) : undefined;
    return parsedError ? parsedError.message : helperText;
  };

  return (
    <VStack>
      <Label size="s">{label}</Label>
      <NumericFormat
        value={parsedValue}
        prefix="$"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale={true}
        customInput={MUITextField}
        {...{
          disabled,
          error: error && !!parseError(error),
          fullWidth: true,
          helperText: getHelperText(error),
          inputProps: createInputProps(props),
          name,
          onChange: (e) => {
            const val = e.target.value;
            const replaced = val ? val.replace(/[^0-9.-]/g, '') : undefined;
            const valNum = replaced ? Number(replaced) * 100 : undefined;
            const formattedValue = formatOnChange(valNum, fieldValue);
            onChange(formattedValue);
          },
          placeholder: placeholder ? placeholder : label,
          rows,
        }}
      />
    </VStack>
  );
};

const createInputProps = (props: CurrencyFieldProps): InputBaseComponentProps => {
  const { ariaLabel, maxLength, onClick, readOnly, type } = props;
  const inputProps: InputBaseComponentProps = {
    'aria-label': ariaLabel,
    'data-testid': getTestId(props),
    maxLength,
    onClick,
    readOnly,
    sx: {
      boxSizing: 'border-box',
      fontSize: '14px',
      height: `${BASE_TEXT_FIELD_HEIGHT}px`,
      '::placeholder': {
        color: palette.controls.outline,
        opacity: '1',
      },
    },
    type,
  };

  return inputProps;
};

export const CurrencyField = ({ name, ...rest }: CurrencyFieldProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <CurrencyFieldInput error={error} name={name} onChange={onChange} value={value} {...rest} />
      )}
    />
  );
};
